/**
 * Analytics Service
 * 
 * Non-blocking event tracking for user behavior and platform events.
 * All tracking methods are fire-and-forget to never impact user experience.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

// Event categories
export type EventCategory = 'acquisition' | 'engagement' | 'transaction' | 'system';

// Standard event names for type safety
export const ANALYTICS_EVENTS = {
  // Acquisition events
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  
  // Buyer Journey - Job Creation
  JOB_STARTED: 'job_started',
  JOB_TAB_COMPLETED: 'job_tab_completed',
  JOB_CREATED: 'job_created',
  
  // Buyer Journey - Specification Creation
  SPEC_STARTED: 'spec_started',
  SPEC_COMPLETED: 'spec_completed',
  
  // Buyer Journey - RFQ Flow
  RFQ_CREATED: 'rfq_created',
  RFQ_DRAFT_SAVED: 'rfq_draft_saved',
  RFQ_PUBLISHED: 'rfq_published',
  RFQ_VIEWED: 'rfq_viewed',
  RFQ_QUESTION_ASKED: 'rfq_question_asked',
  RFQ_QUESTION_ANSWERED: 'rfq_question_answered',
  
  // Buyer Journey - Quote & Award
  QUOTE_VIEWED: 'quote_viewed',
  JOB_AWARDED: 'job_awarded',
  RFQ_AWARD_FEEDBACK_SUBMITTED: 'rfq_award_feedback_submitted',
  
  // Buyer Journey - Execution
  PROJECT_CREATED: 'project_created',
  REPORT_VIEWED: 'report_viewed',
  PO_GENERATED: 'po_generated',
  JOB_COMPLETED: 'job_completed',
  
  // Vendor Journey - RFQ/Job Viewing
  JOB_VIEWED_VENDOR: 'job_viewed_vendor',
  
  // Vendor Journey - Bidding
  BID_STARTED: 'bid_started',
  BID_SUBMITTED: 'bid_submitted',
  BID_WITHDRAWN: 'bid_withdrawn',
  BID_VIEWED: 'bid_viewed',
  QUOTE_WON: 'quote_won',
  QUOTE_LOST: 'quote_lost',
  
  // Vendor Journey - Fulfillment
  SHIPMENT_ADDED: 'shipment_added',
  FILE_UPLOADED: 'file_uploaded',
  MILESTONE_MARKED_COMPLETE: 'milestone_marked_complete',
  JOB_MARKED_COMPLETE: 'job_marked_complete',
  
  // Transaction events
  BID_AWARDED: 'bid_awarded',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_COMPLETED: 'payment_completed',
  PAYMENT_FAILED: 'payment_failed',
  PAYMENT_RECEIVED: 'payment_received',
  DELIVERY_CONFIRMED: 'delivery_confirmed',
  
  // Shared events
  DASHBOARD_VISITED: 'dashboard_visited',
  MESSAGE_SENT: 'message_sent',
  PROFILE_UPDATED: 'profile_updated',
  PROFILE_COMPLETED: 'profile_completed',
  TEAM_MEMBER_INVITED: 'team_member_invited',
  SEARCH_PERFORMED: 'search_performed',
  REVIEW_SUBMITTED: 'review_submitted',
  VENDOR_INVITED: 'vendor_invited',
  
  // System events
  ERROR_OCCURRED: 'error_occurred',
  PAGE_VIEW: 'page_view',
  SESSION_STARTED: 'session_started',
  SESSION_ENDED: 'session_ended',
} as const;

export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

// Event properties interface
export interface AnalyticsEventProperties {
  // Common properties
  user_id?: string;
  organization_id?: string;
  user_role?: 'client' | 'vendor' | 'admin';
  
  // Job properties
  job_id?: string;
  job_public_id?: string;
  category?: string;
  
  // Specification properties
  spec_id?: string;
  spec_category?: string;
  spec_subcategory?: string;
  input_method?: 'form' | 'ai' | 'upload';
  field_count?: number;
  
  // Tab completion properties
  tab_name?: string;
  
  // RFQ properties
  rfq_id?: string;
  rfq_public_id?: string;
  specification_count?: number;
  vendor_criteria?: Record<string, unknown>;
  freight_terms?: 'fob_origin' | 'fob_destination';
  visibility?: 'public' | 'private';
  vendor_count?: number;
  line_item_count?: number;
  
  // Bid properties
  bid_id?: string;
  bid_amount_cents?: number;
  bid_count?: number;
  turnaround_days?: number;
  line_item_count_bid?: number;
  
  // Award properties
  winning_bid_id?: string;
  award_amount_cents?: number;
  
  // Payment properties
  amount_cents?: number;
  payment_method?: string;
  processing_time_ms?: number;
  
  // Shipment properties
  carrier?: string;
  tracking_count?: number;
  
  // File properties
  file_category?: string;
  file_type?: string;
  
  // Milestone properties
  milestone_id?: string;
  milestone_type?: string;
  
  // Report properties
  report_type?: string;
  
  // Search properties
  search_type?: string;
  search_query?: string;
  has_filters?: boolean;
  result_count?: number;
  
  // Review properties
  reviewer_type?: 'client' | 'vendor';
  overall_rating?: number;
  
  // Message properties
  is_flagged?: boolean;
  
  // Profile properties
  profile_type?: 'user' | 'vendor' | 'organization';
  fields_updated?: string[];
  
  // Team properties
  invited_role?: string;
  
  // Dashboard properties
  dashboard_type?: 'client' | 'vendor';
  
  // Login properties
  login_method?: string;
  
  // Acquisition properties
  acquisition_source?: string;
  acquisition_source_detail?: string;
  
  // Onboarding properties
  step_name?: string;
  step_number?: number;
  completion_percentage?: number;
  completion_time_seconds?: number;
  
  // Error properties
  error_type?: string;
  error_message?: string;
  
  // Page view properties
  page_name?: string;
  page_path?: string;
  referrer?: string;
  
  // Allow any additional properties
  [key: string]: unknown;
}

interface AnalyticsEvent {
  name: AnalyticsEventName | string;
  category: EventCategory;
  properties?: AnalyticsEventProperties;
}

interface UserContext {
  userId?: string;
  organizationId?: string;
  userRole?: 'client' | 'vendor' | 'admin';
}

// Generate a session ID that persists for the browser session
function generateSessionId(): string {
  const existingSessionId = sessionStorage.getItem('analytics_session_id');
  if (existingSessionId) {
    return existingSessionId;
  }
  const newSessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem('analytics_session_id', newSessionId);
  return newSessionId;
}

class AnalyticsService {
  private sessionId: string;
  private userContext: UserContext = {};
  private sessionStartTime: number | null = null;

  constructor() {
    this.sessionId = generateSessionId();
  }

  /**
   * Set user context for all subsequent events
   */
  setUserContext(context: UserContext): void {
    this.userContext = { ...this.userContext, ...context };
  }

  /**
   * Clear user context (on logout)
   */
  clearUserContext(): void {
    this.userContext = {};
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Start a new session (call on app load or login)
   */
  startSession(): void {
    this.sessionStartTime = Date.now();
    this.track({
      name: ANALYTICS_EVENTS.SESSION_STARTED,
      category: 'system',
      properties: {
        session_id: this.sessionId,
      },
    });
  }

  /**
   * End the current session (call on logout or tab close)
   */
  endSession(): void {
    const sessionDuration = this.sessionStartTime 
      ? Math.floor((Date.now() - this.sessionStartTime) / 1000)
      : null;
    
    this.track({
      name: ANALYTICS_EVENTS.SESSION_ENDED,
      category: 'system',
      properties: {
        session_id: this.sessionId,
        session_duration_seconds: sessionDuration,
      },
    });
  }

  /**
   * Track an event - non-blocking, fire-and-forget
   */
  track(event: AnalyticsEvent): void {
    // Fire and forget - never block the user
    this.recordEvent(event).catch((err) => {
      // Silent fail in production, log in development
      if (import.meta.env.DEV) {
        console.warn('[Analytics] Failed to record event:', event.name, err);
      }
    });
  }

  /**
   * Track a page view
   */
  page(pageName: string, pagePath?: string): void {
    this.track({
      name: ANALYTICS_EVENTS.PAGE_VIEW,
      category: 'engagement',
      properties: {
        page_name: pageName,
        page_path: pagePath || window.location.pathname,
        referrer: document.referrer || undefined,
      },
    });
  }

  /**
   * Identify a user with traits (called after login/signup)
   */
  identify(userId: string, traits?: Record<string, unknown>): void {
    this.setUserContext({ userId });
    
    // Record the identification as an event for audit trail
    this.track({
      name: 'user_identified',
      category: 'system',
      properties: {
        ...traits,
      },
    });
  }

  /**
   * Internal method to record event to database
   */
  private async recordEvent(event: AnalyticsEvent): Promise<void> {
    const { name, category, properties = {} } = event;
    
    // Merge user context with event properties
    const mergedProperties = {
      ...properties,
      user_id: properties.user_id || this.userContext.userId,
      organization_id: properties.organization_id || this.userContext.organizationId,
      user_role: properties.user_role || this.userContext.userRole,
    };

    const eventData = {
      event_name: name,
      event_category: category,
      user_id: this.userContext.userId || null,
      organization_id: this.userContext.organizationId || null,
      session_id: this.sessionId,
      properties: mergedProperties as Json,
      user_role: this.userContext.userRole || null,
    };

    const { error } = await supabase
      .from('analytics_events')
      .insert([eventData]);

    if (error) {
      throw error;
    }
  }
}

// Singleton instance
export const analytics = new AnalyticsService();

// Helper to get event category from event name
export function getEventCategory(eventName: string): EventCategory {
  if (eventName.includes('signed_up') || eventName.includes('onboarding')) {
    return 'acquisition';
  }
  if (eventName.includes('payment') || eventName.includes('awarded') || eventName.includes('delivery')) {
    return 'transaction';
  }
  if (eventName.includes('error') || eventName.includes('session')) {
    return 'system';
  }
  return 'engagement';
}
